import mapItemTypeToItemRelation, {
    getItemClientId,
} from '../../store-factory/mapItemTypeToItemRelation';
import { shouldShowAttachmentPreviewsForItem } from 'owa-mail-attachment-previews';
import type { TableView, MailFolderTableQuery } from 'owa-mail-list-store';
import {
    type TableQueryType,
    getStore as getListViewStore,
    type SortColumn,
} from 'owa-mail-list-store';
import getTableToRowRelationKey from 'owa-mail-list-store/lib/utils/getTableToRowRelationKey';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import type { ClientItem, LoadConversationItemActionSource } from 'owa-mail-store';
import { DecryptionState } from 'owa-mail-store/lib/store/schema/DecryptionState';
import { type DecryptionFailureReason } from 'owa-mail-store/lib/store/schema/DecryptionFailureReason';
import mailStore from 'owa-mail-store/lib/store/Store';
import getShouldShowRSVPForItemAndPrepareItem from 'owa-listview-rsvp/lib/utils/getShouldShowRSVPForItemAndPrepareItem';
import isSearchResultItemMailboxCertain from 'owa-mail-search/lib/utils/isSearchResultItemMailboxCertain';
import type { ItemRow } from 'owa-graph-schema';
import type Message from 'owa-service/lib/contract/Message';
import { isAccountFeatureEnabled, isGlobalFeatureEnabled } from 'owa-feature-flags';
import { getUserConfiguration } from 'owa-session-store';
import { mutatorAction } from 'satcheljs';
import { getStore as getCategorySortStore } from 'owa-mail-category-sort';

function partialUpdateItem(itemToUpdate: ClientItem, item: ClientItem, tableView: TableView) {
    /**
     * Skip property updates in this block if table is a search table because
     * these items are fetched from 3S APIs and do not include these properties
     * in the response.
     */
    if (tableView.tableQuery.type !== 1) {
        itemToUpdate.ReceivedOrRenewTime = item.ReceivedOrRenewTime;
        itemToUpdate.shouldShowRSVP = item.shouldShowRSVP;
    } else {
        // Search results can come from different mailboxes than the table's owner, so keep a cached
        // item's MailboxInfo refreshed to the newly-resolved value.
        itemToUpdate.MailboxInfo = item.MailboxInfo;
    }

    // Common partial update
    itemToUpdate.Subject = item.Subject;
    itemToUpdate.Importance = item.Importance;
    itemToUpdate.Sensitivity = item.Sensitivity;
    itemToUpdate.HasAttachments = item.HasAttachments;
    itemToUpdate.ItemClass = item.ItemClass;
    itemToUpdate.Flag = item.Flag;
    itemToUpdate.Categories = item.Categories;

    // Mail folder specific updates
    itemToUpdate.ParentFolderId = item.ParentFolderId;
    itemToUpdate.ParentFolderDisplayName = item.ParentFolderDisplayName;
    itemToUpdate.ConversationId = item.ConversationId;
    itemToUpdate.DateTimeReceived = item.DateTimeReceived;
    itemToUpdate.LastModifiedTime = item.LastModifiedTime;
    itemToUpdate.LastMeaningfulDraftSaveTime = item.LastMeaningfulDraftSaveTime;
    itemToUpdate.IsDraft = item.IsDraft;
    itemToUpdate.Size = item.Size;
    itemToUpdate.Preview = item.Preview;
    itemToUpdate.DisplayTo = item.DisplayTo;
    itemToUpdate.IconIndex = item.IconIndex;
    itemToUpdate.shouldShowAttachmentPreviews = item.shouldShowAttachmentPreviews;

    itemToUpdate.loadSource = item.loadSource;

    // Only update return time if item provides value
    if (!!item.ReturnTime) {
        itemToUpdate.ReturnTime = item.ReturnTime;
    }

    if (!!item.HasProcessedSharepointLink) {
        itemToUpdate.HasProcessedSharepointLink = item.HasProcessedSharepointLink;
    }

    if (item.IsCopilotProactiveDraft === true) {
        itemToUpdate.IsCopilotProactiveDraft = true;
    }

    if (item.ProactiveDraftGenerationProperties) {
        itemToUpdate.ProactiveDraftGenerationProperties = item.ProactiveDraftGenerationProperties;
    }

    // Message specific updates
    if (
        !itemToUpdate?.ItemClass || // server treats undefined or null ItemClass as a Message type by default
        itemToUpdate.ItemClass.indexOf('IPM.Schedule.Meeting') !== -1 || // Meeting request messages
        itemToUpdate.ItemClass.indexOf('IPM.Note') !== -1 // Normal messages
    ) {
        const messageToUpdate = itemToUpdate as Message;
        const message = item as Message;

        messageToUpdate.IsRead = message.IsRead;

        if (tableView.tableQuery.type !== 1) {
            // Skip updating the following properties in search as they're not returned
            messageToUpdate.IsReadReceiptRequested = message.IsReadReceiptRequested;
            messageToUpdate.IsDeliveryReceiptRequested = message.IsDeliveryReceiptRequested;
        }

        if (message.CopilotInboxScore && message.CopilotInboxScore > -1) {
            messageToUpdate.CopilotInboxScore = message.CopilotInboxScore;
        }

        if (!!message.CopilotInboxHeadline) {
            messageToUpdate.CopilotInboxHeadline = message.CopilotInboxHeadline;
        }

        if (!!message.CopilotInboxScoreReason) {
            itemToUpdate.CopilotInboxReason = message.CopilotInboxScoreReason;
        }

        if (
            isAccountFeatureEnabled('mon-copilot-needs-reply-nudge', getMailboxInfo(tableView)) &&
            !!item.CopilotNeedsReplyReason
        ) {
            itemToUpdate.CopilotNeedsReplyReason = item.CopilotNeedsReplyReason;
        }

        if (!!message.CopilotPriorityIsUserAdjusted) {
            messageToUpdate.CopilotPriorityIsUserAdjusted = message.CopilotPriorityIsUserAdjusted;
        }
    }
}

/**
 * Adds or updates the item in the mail store
 * This is called whenever a data is fetched from the server and is being stored. This is also called when
 * a rowAdd or rowModified notification occurs
 * @param item to add or update
 * @param tableView where to add or update the item
 * @param state for unit testing
 */
export const addOrUpdateItemDataMutator: (
    serviceItem: ItemRow,
    tableView: TableView,
    loadSource: LoadConversationItemActionSource
) => void = mutatorAction(
    'addOrUpdateItemData',
    function addOrUpdateItemData(
        serviceItem: ItemRow,
        tableView: TableView,
        loadSource: LoadConversationItemActionSource
    ): void {
        const itemId = serviceItem.ItemId.Id;
        // Search results can come from different mailboxes than the table's owner, so resolve each
        // item's MailboxInfo via getItemClientId instead of assuming the table's mailbox.
        const isSearch = tableView.tableQuery.type === 1;
        const mailboxInfo = isSearch
            ? getItemClientId(serviceItem, tableView).mailboxInfo
            : getMailboxInfo(tableView);
        // Non-search tables own their items, so the table mailbox is authoritative. A search result
        // is only certain when its mailbox was resolved authoritatively (archive fanout / explicit
        // mailboxInfo); otherwise it was defaulted to the table mailbox and must not be prefetched.
        const isMailboxCertain =
            !isGlobalFeatureEnabled('tri-rsvpPrefetch-mailboxRouting') ||
            (isSearch ? isSearchResultItemMailboxCertain(serviceItem) : true);

        const clientItemToMerge = <ClientItem>{
            ...serviceItem,
            MailboxInfo: mailboxInfo,
            shouldShowAttachmentPreviews:
                getUserConfiguration().UserOptions?.ShowInlinePreviews &&
                shouldShowAttachmentPreviewsForItem(serviceItem, tableView.tableQuery),
            shouldShowRSVP: getShouldShowRSVPForItemAndPrepareItem(
                serviceItem,
                tableView,
                mailboxInfo,
                isMailboxCertain
            ),
            CopilotInboxReason: (serviceItem as Message)?.CopilotInboxScoreReason ?? '',
            loadSource,
            // Initialize AddinData AFTER spreading serviceItem to ensure our default is used.
            // Satchel will make this observable when stored - do not use makeAutoObservable here.
            AddinData: {
                DecryptionState: DecryptionState.NotApplicable,
                DecryptionFailureReason: 0,
            },
        };

        const itemInCache = mailStore().items.get(itemId) as ClientItem | undefined;
        if (itemInCache) {
            if (itemInCache.IsDraft && !clientItemToMerge.IsDraft) {
                // If the item in the cache is a draft and the new item is not a draft,
                // delete item from cache since its body is out of date
                mailStore().items.delete(itemId);
            } else {
                // If the item is in the cache, partial update it with the new properties
                partialUpdateItem(itemInCache, clientItemToMerge, tableView);
            }
        } else {
            // Otherwise add it to the item cache
            mailStore().items.set(itemId, clientItemToMerge);
        }

        const tableItemRelationKey = getTableToRowRelationKey(
            serviceItem.InstanceKey ?? '',
            tableView.id
        );
        // Create or update the relation object
        const newRelation = mapItemTypeToItemRelation(serviceItem, tableView);

        const tableViewItemRelations = getListViewStore().tableViewItemRelations;
        const oldRelation = tableViewItemRelations.get(tableItemRelationKey);
        if (oldRelation) {
            // if we already have the relation then just update the values so we don't have to
            // make the whole object observable
            Object.assign(oldRelation, newRelation);
        } else {
            getListViewStore().tableViewItemRelations.set(tableItemRelationKey, newRelation);
        }

        // Add category data specific to the item (for Category sort table)
        if (tableView?.tableQuery.type === 0) {
            const mailTableQuery = tableView?.tableQuery as MailFolderTableQuery;

            if (mailTableQuery.sortBy?.sortColumn === 14 && serviceItem.InstanceKey) {
                getCategorySortStore(getMailboxInfo(tableView)).categoryByRowKey.set(
                    serviceItem.InstanceKey,
                    serviceItem.Categories?.[0] ?? ''
                );
            }
        }
    }
);
